import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Building2, User, Mail, Phone, MapPin, FileText, Globe, ChevronLeft, CheckCircle2, Clock, XCircle, Eye, EyeOff, RefreshCw, MailCheck, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const PROPERTY_TYPES = [
  { id: 'hotel', label: 'Hotel' },
  { id: 'resort', label: 'Resort' },
  { id: 'apartment', label: 'Apartment/Homestay' },
  { id: 'villa', label: 'Villa' },
  { id: 'guesthouse', label: 'Guest House' }
];

const INDIAN_STATES = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
  'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka',
  'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram',
  'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu',
  'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
  'Delhi', 'Jammu and Kashmir', 'Ladakh', 'Puducherry'
];

export default function ListPropertyPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(1); // 1: Form, 2: Success, 2.5: Verify Email, 3: Check Status
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [checkEmail, setCheckEmail] = useState('');
  const [statusResult, setStatusResult] = useState(null);
  const [verificationEmail, setVerificationEmail] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    owner_name: '',
    email: '',
    phone: '',
    password: '',
    confirm_password: '',
    company_name: '',
    company_address: '',
    city: '',
    state: '',
    pincode: '',
    gst_number: '',
    pan_number: '',
    estimated_properties: 1,
    property_types: ['hotel'],
    website: '',
    description: ''
  });

  const [errors, setErrors] = useState({});

  // Handle email verification from URL params
  useEffect(() => {
    const verifyParam = searchParams.get('verify');
    const emailParam = searchParams.get('email');
    const tokenParam = searchParams.get('token');
    
    if (verifyParam === 'true' && emailParam && tokenParam) {
      verifyEmailToken(emailParam, tokenParam);
    }
  }, [searchParams]);

  const verifyEmailToken = async (email, token) => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/api/portal/verify-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, token })
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Verification failed');
      }

      toast.success('Email verified successfully!');
      setStep(2.75); // Show verified success page
    } catch (error) {
      toast.error(error.message);
      setStep(3); // Go to check status
      setCheckEmail(email);
    } finally {
      setLoading(false);
    }
  };

  const resendVerification = async () => {
    if (!verificationEmail) {
      toast.error('Please enter your email');
      return;
    }
    
    setResendLoading(true);
    try {
      const response = await fetch(`${API}/api/portal/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: verificationEmail })
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to resend');
      }

      toast.success('Verification email sent! Please check your inbox.');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setResendLoading(false);
    }
  };

  const validateStep1 = () => {
    const newErrors = {};
    if (!formData.owner_name.trim()) newErrors.owner_name = 'Name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Invalid email format';
    if (!formData.phone.trim()) newErrors.phone = 'Phone is required';
    else if (!/^\d{10}$/.test(formData.phone.replace(/\D/g, ''))) newErrors.phone = 'Invalid phone number';
    if (!formData.password) newErrors.password = 'Password is required';
    else if (formData.password.length < 6) newErrors.password = 'Password must be at least 6 characters';
    if (formData.password !== formData.confirm_password) newErrors.confirm_password = 'Passwords do not match';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = () => {
    const newErrors = {};
    if (!formData.company_name.trim()) newErrors.company_name = 'Company name is required';
    if (!formData.company_address.trim()) newErrors.company_address = 'Address is required';
    if (!formData.city.trim()) newErrors.city = 'City is required';
    if (!formData.state) newErrors.state = 'State is required';
    if (!formData.pincode.trim()) newErrors.pincode = 'Pincode is required';
    else if (!/^\d{6}$/.test(formData.pincode)) newErrors.pincode = 'Invalid pincode';
    if (formData.property_types.length === 0) newErrors.property_types = 'Select at least one property type';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateStep2()) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${API}/api/portal/register-property`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          owner_name: formData.owner_name,
          email: formData.email,
          phone: formData.phone,
          password: formData.password,
          company_name: formData.company_name,
          company_address: formData.company_address,
          city: formData.city,
          state: formData.state,
          pincode: formData.pincode,
          gst_number: formData.gst_number || null,
          pan_number: formData.pan_number || null,
          estimated_properties: formData.estimated_properties,
          property_types: formData.property_types,
          website: formData.website || null,
          description: formData.description || null
        })
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Registration failed');
      }

      toast.success('Registration submitted! Please check your email to verify.');
      setVerificationEmail(formData.email);
      setStep(2); // Show success page with email verification info
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const checkStatus = async () => {
    if (!checkEmail.trim()) {
      toast.error('Please enter your email');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API}/api/portal/registration-status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: checkEmail })
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Not found');
      }

      setStatusResult(data);
    } catch (error) {
      toast.error(error.message);
      setStatusResult(null);
    } finally {
      setLoading(false);
    }
  };

  const handlePropertyTypeChange = (typeId) => {
    setFormData(prev => ({
      ...prev,
      property_types: prev.property_types.includes(typeId)
        ? prev.property_types.filter(t => t !== typeId)
        : [...prev.property_types, typeId]
    }));
  };

  // Step 1: Personal Details
  const renderStep1 = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="owner_name">Full Name *</Label>
          <div className="relative">
            <User className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
            <Input
              id="owner_name"
              placeholder="Enter your full name"
              className="pl-10"
              value={formData.owner_name}
              onChange={(e) => setFormData({...formData, owner_name: e.target.value})}
              data-testid="owner-name-input"
            />
          </div>
          {errors.owner_name && <p className="text-xs text-red-500">{errors.owner_name}</p>}
        </div>

        <div className="space-y-2">
          <Label htmlFor="email">Email Address *</Label>
          <div className="relative">
            <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
            <Input
              id="email"
              type="email"
              placeholder="you@company.com"
              className="pl-10"
              value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
              data-testid="email-input"
            />
          </div>
          {errors.email && <p className="text-xs text-red-500">{errors.email}</p>}
        </div>

        <div className="space-y-2">
          <Label htmlFor="phone">Phone Number *</Label>
          <div className="relative">
            <Phone className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
            <Input
              id="phone"
              placeholder="10-digit mobile number"
              className="pl-10"
              value={formData.phone}
              onChange={(e) => setFormData({...formData, phone: e.target.value})}
              data-testid="phone-input"
            />
          </div>
          {errors.phone && <p className="text-xs text-red-500">{errors.phone}</p>}
        </div>

        <div className="space-y-2">
          <Label htmlFor="password">Password *</Label>
          <div className="relative">
            <Input
              id="password"
              type={showPassword ? 'text' : 'password'}
              placeholder="Min 6 characters"
              className="pr-10"
              value={formData.password}
              onChange={(e) => setFormData({...formData, password: e.target.value})}
              data-testid="password-input"
            />
            <button
              type="button"
              className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          {errors.password && <p className="text-xs text-red-500">{errors.password}</p>}
        </div>

        <div className="space-y-2 md:col-span-2">
          <Label htmlFor="confirm_password">Confirm Password *</Label>
          <Input
            id="confirm_password"
            type="password"
            placeholder="Re-enter password"
            value={formData.confirm_password}
            onChange={(e) => setFormData({...formData, confirm_password: e.target.value})}
            data-testid="confirm-password-input"
          />
          {errors.confirm_password && <p className="text-xs text-red-500">{errors.confirm_password}</p>}
        </div>
      </div>

      <div className="flex justify-end pt-4">
        <Button 
          onClick={() => validateStep1() && setStep(1.5)}
          className="bg-gradient-to-r from-blue-600 to-blue-700"
          data-testid="next-step-btn"
        >
          Next: Company Details
        </Button>
      </div>
    </div>
  );

  // Step 1.5: Company Details
  const renderStep1_5 = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2 md:col-span-2">
          <Label htmlFor="company_name">Company/Property Name *</Label>
          <div className="relative">
            <Building2 className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
            <Input
              id="company_name"
              placeholder="Your hotel/company name"
              className="pl-10"
              value={formData.company_name}
              onChange={(e) => setFormData({...formData, company_name: e.target.value})}
              data-testid="company-name-input"
            />
          </div>
          {errors.company_name && <p className="text-xs text-red-500">{errors.company_name}</p>}
        </div>

        <div className="space-y-2 md:col-span-2">
          <Label htmlFor="company_address">Address *</Label>
          <div className="relative">
            <MapPin className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
            <Textarea
              id="company_address"
              placeholder="Full address"
              className="pl-10 min-h-[80px]"
              value={formData.company_address}
              onChange={(e) => setFormData({...formData, company_address: e.target.value})}
              data-testid="address-input"
            />
          </div>
          {errors.company_address && <p className="text-xs text-red-500">{errors.company_address}</p>}
        </div>

        <div className="space-y-2">
          <Label htmlFor="city">City *</Label>
          <Input
            id="city"
            placeholder="City"
            value={formData.city}
            onChange={(e) => setFormData({...formData, city: e.target.value})}
            data-testid="city-input"
          />
          {errors.city && <p className="text-xs text-red-500">{errors.city}</p>}
        </div>

        <div className="space-y-2">
          <Label htmlFor="state">State *</Label>
          <select
            id="state"
            className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
            value={formData.state}
            onChange={(e) => setFormData({...formData, state: e.target.value})}
            data-testid="state-select"
          >
            <option value="">Select State</option>
            {INDIAN_STATES.map(state => (
              <option key={state} value={state}>{state}</option>
            ))}
          </select>
          {errors.state && <p className="text-xs text-red-500">{errors.state}</p>}
        </div>

        <div className="space-y-2">
          <Label htmlFor="pincode">Pincode *</Label>
          <Input
            id="pincode"
            placeholder="6-digit pincode"
            value={formData.pincode}
            onChange={(e) => setFormData({...formData, pincode: e.target.value})}
            data-testid="pincode-input"
          />
          {errors.pincode && <p className="text-xs text-red-500">{errors.pincode}</p>}
        </div>

        <div className="space-y-2">
          <Label htmlFor="gst_number">GST Number (Optional)</Label>
          <div className="relative">
            <FileText className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
            <Input
              id="gst_number"
              placeholder="22AAAAA0000A1Z5"
              className="pl-10"
              value={formData.gst_number}
              onChange={(e) => setFormData({...formData, gst_number: e.target.value.toUpperCase()})}
              data-testid="gst-input"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="pan_number">PAN Number (Optional)</Label>
          <Input
            id="pan_number"
            placeholder="ABCDE1234F"
            value={formData.pan_number}
            onChange={(e) => setFormData({...formData, pan_number: e.target.value.toUpperCase()})}
            data-testid="pan-input"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="website">Website (Optional)</Label>
          <div className="relative">
            <Globe className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
            <Input
              id="website"
              placeholder="https://yourhotel.com"
              className="pl-10"
              value={formData.website}
              onChange={(e) => setFormData({...formData, website: e.target.value})}
              data-testid="website-input"
            />
          </div>
        </div>
      </div>

      <div className="flex justify-between pt-4">
        <Button 
          variant="outline"
          onClick={() => setStep(1)}
        >
          <ChevronLeft className="h-4 w-4 mr-1" /> Back
        </Button>
        <Button 
          onClick={() => validateStep2() && setStep(1.75)}
          className="bg-gradient-to-r from-blue-600 to-blue-700"
        >
          Next: Property Details
        </Button>
      </div>
    </div>
  );

  // Step 1.75: Property Details
  const renderStep1_75 = () => (
    <div className="space-y-4">
      <div className="space-y-4">
        <div className="space-y-2">
          <Label>Property Types *</Label>
          <p className="text-xs text-gray-500">Select the types of properties you manage</p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 pt-2">
            {PROPERTY_TYPES.map(type => (
              <label
                key={type.id}
                className={`
                  flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-all
                  ${formData.property_types.includes(type.id) 
                    ? 'border-blue-500 bg-blue-50 text-blue-700' 
                    : 'border-gray-200 hover:border-gray-300'}
                `}
              >
                <Checkbox
                  checked={formData.property_types.includes(type.id)}
                  onCheckedChange={() => handlePropertyTypeChange(type.id)}
                />
                <span className="text-sm font-medium">{type.label}</span>
              </label>
            ))}
          </div>
          {errors.property_types && <p className="text-xs text-red-500">{errors.property_types}</p>}
        </div>

        <div className="space-y-2">
          <Label htmlFor="estimated_properties">Estimated Number of Properties</Label>
          <Input
            id="estimated_properties"
            type="number"
            min="1"
            value={formData.estimated_properties}
            onChange={(e) => setFormData({...formData, estimated_properties: parseInt(e.target.value) || 1})}
            data-testid="properties-count-input"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="description">Tell us about your property (Optional)</Label>
          <Textarea
            id="description"
            placeholder="Brief description of your property, amenities, USP, etc."
            className="min-h-[100px]"
            value={formData.description}
            onChange={(e) => setFormData({...formData, description: e.target.value})}
            data-testid="description-input"
          />
        </div>
      </div>

      <div className="flex justify-between pt-4">
        <Button 
          variant="outline"
          onClick={() => setStep(1.5)}
        >
          <ChevronLeft className="h-4 w-4 mr-1" /> Back
        </Button>
        <Button 
          onClick={handleSubmit}
          disabled={loading}
          className="bg-gradient-to-r from-green-600 to-green-700"
          data-testid="submit-registration-btn"
        >
          {loading ? 'Submitting...' : 'Submit Registration'}
        </Button>
      </div>
    </div>
  );

  // Success Page - Email Verification Needed
  const renderSuccess = () => (
    <div className="text-center py-8 space-y-6">
      <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto">
        <Mail className="h-10 w-10 text-blue-600" />
      </div>
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Verify Your Email</h2>
        <p className="text-gray-600 mt-2">
          We've sent a verification link to <strong>{verificationEmail}</strong>
        </p>
      </div>
      <div className="bg-blue-50 rounded-lg p-4 max-w-md mx-auto">
        <h3 className="font-semibold text-blue-900">Next Steps:</h3>
        <ul className="text-sm text-blue-800 mt-2 space-y-1 text-left">
          <li>1. Check your email inbox (and spam folder)</li>
          <li>2. Click the verification link in the email</li>
          <li>3. Once verified, our team will review your application</li>
          <li>4. You'll receive approval notification via email</li>
        </ul>
      </div>
      <div className="bg-gray-50 rounded-lg p-4 max-w-md mx-auto">
        <p className="text-sm text-gray-600 mb-3">Didn't receive the email?</p>
        <div className="flex gap-2 justify-center">
          <Input
            type="email"
            placeholder="Enter your email"
            value={verificationEmail}
            onChange={(e) => setVerificationEmail(e.target.value)}
            className="max-w-[200px]"
          />
          <Button 
            variant="outline" 
            onClick={resendVerification}
            disabled={resendLoading}
          >
            {resendLoading ? <RefreshCw className="h-4 w-4 animate-spin" /> : 'Resend'}
          </Button>
        </div>
      </div>
      <div className="flex flex-col sm:flex-row gap-3 justify-center pt-4">
        <Button variant="outline" onClick={() => navigate('/partner')}>
          Back to Partner Portal
        </Button>
        <Button onClick={() => setStep(3)} className="bg-blue-600">
          Check Application Status
        </Button>
      </div>
    </div>
  );

  // Email Verified Success Page
  const renderVerifiedSuccess = () => (
    <div className="text-center py-8 space-y-6">
      <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto">
        <MailCheck className="h-10 w-10 text-green-600" />
      </div>
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Email Verified!</h2>
        <p className="text-gray-600 mt-2">
          Your email has been verified successfully.
        </p>
      </div>
      <div className="bg-green-50 rounded-lg p-4 max-w-md mx-auto">
        <h3 className="font-semibold text-green-900">What happens next?</h3>
        <ul className="text-sm text-green-800 mt-2 space-y-1 text-left">
          <li>• Our team will review your application</li>
          <li>• You'll receive an email once approved</li>
          <li>• Login and start adding your properties</li>
        </ul>
      </div>
      <div className="flex flex-col sm:flex-row gap-3 justify-center pt-4">
        <Button variant="outline" onClick={() => navigate('/partner')}>
          Back to Partner Portal
        </Button>
        <Button onClick={() => setStep(3)} className="bg-blue-600">
          Check Application Status
        </Button>
      </div>
    </div>
  );

  // Check Status Page
  const renderCheckStatus = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-xl font-bold">Check Application Status</h2>
        <p className="text-gray-600 text-sm mt-1">Enter your registered email to check status</p>
      </div>

      <div className="flex gap-2">
        <Input
          type="email"
          placeholder="Enter your email"
          value={checkEmail}
          onChange={(e) => setCheckEmail(e.target.value)}
          data-testid="check-status-email"
        />
        <Button onClick={checkStatus} disabled={loading} data-testid="check-status-btn">
          {loading ? 'Checking...' : 'Check'}
        </Button>
      </div>

      {statusResult && (
        <Card className={`
          ${statusResult.status === 'approved' ? 'border-green-200 bg-green-50' : ''}
          ${statusResult.status === 'pending' ? 'border-yellow-200 bg-yellow-50' : ''}
          ${statusResult.status === 'rejected' ? 'border-red-200 bg-red-50' : ''}
        `}>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              {statusResult.status === 'approved' && <CheckCircle2 className="h-8 w-8 text-green-600" />}
              {statusResult.status === 'pending' && <Clock className="h-8 w-8 text-yellow-600" />}
              {statusResult.status === 'rejected' && <XCircle className="h-8 w-8 text-red-600" />}
              <div>
                <p className="font-semibold">{statusResult.company_name}</p>
                <p className="text-sm text-gray-600">ID: {statusResult.registration_id}</p>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t space-y-3">
              {/* Email Verification Status */}
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Email Verified</span>
                <div className="flex items-center gap-2">
                  <span className={`
                    px-3 py-1 rounded-full text-sm font-medium flex items-center gap-1
                    ${statusResult.email_verified ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'}
                  `}>
                    {statusResult.email_verified ? (
                      <><MailCheck className="h-3 w-3" /> Verified</>
                    ) : (
                      <><Mail className="h-3 w-3" /> Not Verified</>
                    )}
                  </span>
                  {!statusResult.email_verified && (
                    <button
                      onClick={() => {
                        setVerificationEmail(checkEmail);
                        resendVerification();
                      }}
                      disabled={resendLoading}
                      className="p-1.5 rounded-full bg-orange-100 hover:bg-orange-200 text-orange-600 transition-colors disabled:opacity-50"
                      title="Resend verification email"
                    >
                      {resendLoading ? (
                        <RefreshCw className="h-4 w-4 animate-spin" />
                      ) : (
                        <Send className="h-4 w-4" />
                      )}
                    </button>
                  )}
                </div>
              </div>
              {/* Application Status */}
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Application Status</span>
                <span className={`
                  px-3 py-1 rounded-full text-sm font-medium
                  ${statusResult.status === 'approved' ? 'bg-green-100 text-green-700' : ''}
                  ${statusResult.status === 'pending' ? 'bg-yellow-100 text-yellow-700' : ''}
                  ${statusResult.status === 'rejected' ? 'bg-red-100 text-red-700' : ''}
                `}>
                  {statusResult.status.toUpperCase()}
                </span>
              </div>
              {/* Email not verified warning */}
              {!statusResult.email_verified && statusResult.status === 'pending' && (
                <div className="mt-3 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                  <p className="text-sm text-orange-800 mb-2">
                    ⚠️ Please verify your email to complete your registration.
                  </p>
                  <div className="flex gap-2">
                    <Button 
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setVerificationEmail(checkEmail);
                        resendVerification();
                      }}
                      disabled={resendLoading}
                      className="text-orange-700 border-orange-300"
                    >
                      {resendLoading ? <RefreshCw className="h-3 w-3 animate-spin mr-1" /> : <Mail className="h-3 w-3 mr-1" />}
                      Resend Verification Email
                    </Button>
                  </div>
                </div>
              )}
              {statusResult.status === 'approved' && (
                <div className="mt-3 p-3 bg-green-100 rounded-lg">
                  <p className="text-sm text-green-800">
                    🎉 Your registration has been approved! You can now{' '}
                    <button 
                      className="underline font-medium"
                      onClick={() => navigate('/partner')}
                    >
                      login to admin panel
                    </button>
                    {' '}and start adding your properties.
                  </p>
                </div>
              )}
              {statusResult.status === 'rejected' && statusResult.rejection_reason && (
                <div className="mt-3 p-3 bg-red-100 rounded-lg">
                  <p className="text-sm text-red-800">
                    <strong>Reason:</strong> {statusResult.rejection_reason}
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="text-center">
        <Button variant="link" onClick={() => { setStep(1); setStatusResult(null); setCheckEmail(''); }}>
          Submit a new registration
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
      {/* Header */}
      <header className="bg-white/10 backdrop-blur-md border-b border-white/10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <button 
            onClick={() => navigate('/partner')}
            className="flex items-center gap-2 text-white hover:text-blue-300 transition-colors"
          >
            <ChevronLeft className="h-5 w-5" />
            <span className="font-semibold">Back to Portal</span>
          </button>
          <Button 
            variant="outline" 
            size="sm"
            className="text-white border-white/30 hover:bg-white/10"
            onClick={() => setStep(3)}
          >
            Check Status
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-2xl mx-auto px-4 py-8">
        <Card className="shadow-2xl">
          <CardHeader className="text-center border-b">
            <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-700 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Building2 className="h-8 w-8 text-white" />
            </div>
            <CardTitle className="text-2xl">List Your Property</CardTitle>
            <CardDescription>
              Join our platform and reach thousands of travelers
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            {/* Progress Indicator */}
            {step < 2 && (
              <div className="mb-6">
                <div className="flex items-center justify-between text-sm mb-2">
                  <span className={step >= 1 ? 'text-blue-600 font-medium' : 'text-gray-400'}>Personal</span>
                  <span className={step >= 1.5 ? 'text-blue-600 font-medium' : 'text-gray-400'}>Company</span>
                  <span className={step >= 1.75 ? 'text-blue-600 font-medium' : 'text-gray-400'}>Property</span>
                </div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-blue-500 to-blue-600 transition-all duration-300"
                    style={{ width: step === 1 ? '33%' : step === 1.5 ? '66%' : '100%' }}
                  />
                </div>
              </div>
            )}

            {step === 1 && renderStep1()}
            {step === 1.5 && renderStep1_5()}
            {step === 1.75 && renderStep1_75()}
            {step === 2 && renderSuccess()}
            {step === 2.75 && renderVerifiedSuccess()}
            {step === 3 && renderCheckStatus()}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
